import { Tag } from '../../types/audience.types';
import type { BroadcastStatus, ScheduleData } from '../types/broadcast.types';
import type { FlowSummary } from '@/lib/api/email-flows/types';
import { formatDistanceToNowStrict } from 'date-fns';
import { parseTimeFromAPI } from '@/lib/helpers';
import { formatEmailSubjectVariables } from '../../logic/templateVariables.logic';

/**
 * Get broadcast display name (uses email subject or "Broadcast")
 * Strips HTML tags, decodes HTML entities, and formats template variables for display
 */
export function getBroadcastName(broadcast: FlowSummary): string {
    if (broadcast.flow_emails?.[0]?.data?.subject) {
        return formatEmailSubjectVariables(broadcast.flow_emails[0].data.subject);
    }
    return 'Broadcast';
}

/**
 * Get broadcast status text for display
 *
 * Note: We check status === 'draft' first to handle duplicated broadcasts
 * that have timestamps copied from the original but are actually drafts.
 */
export function getBroadcastStatusText(broadcast: FlowSummary): string {
    // If status is draft, always show "Draft" (handles duplicated broadcasts)
    if (broadcast.status === 'draft') {
        return 'Draft';
    }

    // Check if fully sent (has sent_timestamp and status is published)
    if (isBroadcastSent(broadcast)) {
        const timestamp = broadcast.sent_timestamp;
        if (!timestamp) return 'Sent';

        const now = Date.now();
        if (now - timestamp < 60000) {
            return 'Sent just now';
        }
        return `Sent ${formatDistanceToNowStrict(timestamp, { addSuffix: true })}`;
    }

    // Check if currently sending (started but not sent)
    if (isBroadcastSending(broadcast)) {
        return 'Sending';
    }

    // Check if scheduled (published with schedule but not started)
    if (isBroadcastScheduled(broadcast)) {
        const timestamp = broadcast.scheduled_timestamp;
        if (!timestamp) return 'Scheduled';

        return `Scheduled ${formatDistanceToNowStrict(timestamp, { addSuffix: true })}`;
    }

    return 'Draft';
}

/**
 * Check if broadcast has been sent
 * A broadcast is sent if it has a sent_timestamp AND status is published
 * (We check status to handle duplicated broadcasts that copied sent_timestamp)
 */
export function isBroadcastSent(broadcast: FlowSummary): boolean {
    return !!broadcast.sent_timestamp && broadcast.status === 'published';
}

/**
 * Check if broadcast is currently sending
 * A broadcast is sending if it has started but doesn't have a sent_timestamp yet
 */
export function isBroadcastSending(broadcast: FlowSummary): boolean {
    return !!broadcast.data?.started && !broadcast.sent_timestamp;
}

/**
 * Check if broadcast is scheduled (published but not started)
 * A broadcast is scheduled if it's published, has a scheduled_timestamp, but hasn't started yet
 */
export function isBroadcastScheduled(broadcast: FlowSummary): boolean {
    return (
        !!broadcast.flow_emails &&
        broadcast.flow_emails.length > 0 &&
        broadcast.status === 'published' &&
        !!broadcast.scheduled_timestamp &&
        !broadcast.data?.started
    );
}

/**
 * Check if broadcast can be edited
 * A broadcast cannot be edited if it has been sent or has started sending
 *
 * Note: We check status === 'draft' first because the backend's duplicate function
 * doesn't clear sent_timestamp/scheduled_timestamp, causing duplicated broadcasts
 * to appear as sent even though they're drafts. This is a backend bug workaround.
 * TODO: Fix backend in the future [stan-flask]
 */
export function isBroadcastEditable(broadcast: FlowSummary): boolean {
    // If status is draft, it's always editable (handles duplicated broadcasts)
    if (broadcast.status === 'draft') {
        return true;
    }

    // If it has been sent, it's not editable
    if (broadcast.sent_timestamp) {
        return false;
    }

    // If it has started sending, it's not editable (backend requirement)
    if (broadcast.data?.started) {
        return false;
    }

    return true;
}

/**
 * Get broadcast status
 * Returns the badge variant to use for display
 */
export function getBroadcastStatus(broadcast: FlowSummary): BroadcastStatus {
    // If status is draft, always return 'draft' (handles duplicated broadcasts)
    if (broadcast.status === 'draft') {
        return 'draft';
    }

    if (isBroadcastSent(broadcast)) return 'sent';
    if (isBroadcastSending(broadcast)) return 'sent'; // Show as 'sent' badge for sending state
    if (isBroadcastScheduled(broadcast)) return 'scheduled';
    return 'draft';
}

/**
 * Compute all tags (include + exclude with excluded flag)
 * Reuses the combineTags logic from audienceBuilder
 */
export function computeAllTags(includedTags: Tag[], excludedTags: Tag[]): Tag[] {
    const tags: Tag[] = [];

    // Add included tags
    includedTags.forEach(tag => {
        tags.push({ ...tag });
    });

    // Add excluded tags with excluded flag
    excludedTags.forEach(tag => {
        tags.push({ ...tag, excluded: 1 });
    });

    return tags;
}

/**
 * Format schedule for API
 */
export function formatScheduleForAPI(schedule: ScheduleData): {
    date: string;
    time: string;
    timezone: string;
} {
    // Convert time number (0-23.99) to HH:MM format
    const hours = Math.floor(schedule.selectedTime);
    const minutes = Math.round((schedule.selectedTime - hours) * 60);
    const time = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    return {
        date: schedule.selectedDate,
        time,
        timezone: schedule.selectedTimezone,
    };
}

/**
 * Parse schedule from API
 */
export function parseScheduleFromAPI(data: FlowSummary['data']): ScheduleData | null {
    if (!data?.specific_time) {
        return null;
    }

    const specificTime = data.specific_time;

    // Parse time string (e.g., "06:00 PM") to 24-hour number
    const selectedTime = parseTimeFromAPI(specificTime.time || '12:00 AM');

    return {
        selectedDate: specificTime.date || '',
        selectedTime,
        selectedTimezone: specificTime.timezone || '',
    };
}

/**
 * Get analytics stats for broadcast
 */
export function getAnalyticsStats(broadcast: FlowSummary): {
    sent: { count: number; rate: string };
    opens: { count: number; rate: string; total: number };
    clicks: { count: number; rate: string; total: number };
} {
    const analytics = broadcast.analytics || {
        sent: 0,
        opened: 0,
        clicked: 0,
        sales: 0,
    };

    const sent = analytics.sent ?? 0;
    const opened = analytics.opened || 0;
    const clicked = analytics.clicked || 0;

    const openRate = sent > 0 ? ((opened / sent) * 100).toFixed(1) : '0.0';
    const clickRate = sent > 0 ? ((clicked / sent) * 100).toFixed(1) : '0.0';

    return {
        sent: { count: sent, rate: '100%' },
        opens: { count: opened, rate: `${openRate}%`, total: sent },
        clicks: { count: clicked, rate: `${clickRate}%`, total: sent },
    };
}
